import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { PrescriptionResponse, PrescriptionsSearch } from '@hm/core/models/POC-prescription.interface';
import { PHARMACIES_LATITUDE, PHARMACIES_LONGITUDE, PHARMACIES_MAX_SEARCH_DISTANCE_METERS } from '@hm/core/models/pharmacies.const';

import { environment } from '@hm/environments/environment';

import { adaptPlainObjectToCamelCase } from '@lib/commons/util-functions/plain-object-to-pascal.function';

import { DoctorWithScheduleModel, ServiceScheduleSlotsModel } from '@lib/core/models/doctor.interface';
import { LimitRule } from '@lib/core/models/insurance-package.interface';
import { LaboratoriesRequestModel, LaboratoriesResponseModel } from '@lib/core/models/laboratory.interface';
import { PagedResponse } from '@lib/core/models/paged-response';
import { PharmacyBasketModel } from '@lib/core/models/pharmacy.interface';
import {
  GenericServiceValue,
  PersonalDoctorMetadata,
  PersonalDoctorValue,
  PharmacologicalItemMetaData,
  PharmacologicalTreatmentValue,
  Prescription,
  PrescriptionFormValue,
  PrescriptionItem,
  ResponseMorionItem,
  ServiceMetadata,
} from '@lib/core/models/prescription.interface';
import { LiloDrugSearchResponse, MedicationBasketRequest, ServiceDictionary } from '@lib/core/models/protocol.interface';

import { FranchiseSearchRequest, FranchiseSearchResponse } from '../models/franchises.interface';

export interface PrescriptionPlanningRequestModel<T> {
  timeSlotId?: string;
  prescriptionIds: string[]; // List of GUIDs as strings
  partnerId: string; // GUID as a string
  partnerLocationId: string; // GUID as a string
  executeIntegration: boolean; // Boolean flag
  doctorId?: string; // Optional string
  doctorName?: string; // Optional string
  startDate?: string; // Optional ISO date string
  endDate?: string; // Optional ISO date string
  price?: number; // Optional decimal as a number
  details: T; // JSON object
}

@Injectable({
  providedIn: 'root',
})
export class PrescriptionsService {
  private http: HttpClient = inject(HttpClient);

  createPrescription(memberId: string, applicationId: string, prescription: any): Observable<Prescription> {
    return this.http.post<Prescription>(`${environment.healthManagerApiUrl}/members/${memberId}/guarantee-applications/${applicationId}/prescriptions`, prescription);
  }

  updatePrescription(memberId: string, applicationId: string, prescriptionId: string, prescription: any): Observable<Prescription> {
    return this.http.put<Prescription>(
      `${environment.healthManagerApiUrl}/members/${memberId}/guarantee-applications/${applicationId}/prescriptions/${prescriptionId}`,
      prescription
    );
  }

  processPrescriptionDraftsCreate(memberId: string, drafts: unknown[]): Observable<unknown> {
    return this.http.post<unknown>(`${environment.healthManagerApiUrl}/members/${memberId}/prescriptions/bulk/create`, drafts);
  }

  processPrescriptionsDraftsUpdate(memberId: string, drafts: unknown[]): Observable<unknown> {
    return this.http.put<unknown>(`${environment.healthManagerApiUrl}/members/${memberId}/prescriptions/bulk/update`, drafts);
  }

  planPrescription<T>(data: PrescriptionPlanningRequestModel<T>) {
    return this.http.post(`${environment.healthManagerApiUrl}/members/prescriptions/create`, data);
  }

  searchLaboratories(request: LaboratoriesRequestModel) {
    return this.http.post<LaboratoriesResponseModel>(`${environment.healthManagerApiUrl}/laboratories/search`, request);
  }

  searchMedicalTreatment(searchString: string): Observable<LiloDrugSearchResponse[]> {
    const payload = {
      search: searchString,
    };
    return this.http.post<LiloDrugSearchResponse[]>(`${environment.healthManagerApiUrl}/dictionaries/drugs`, payload);
  }

  searchLaboratoryService(searchString: string): Observable<PagedResponse<ServiceDictionary>> {
    const options = {
      params: new HttpParams().set('nameUa', searchString),
    };
    return this.http.get<PagedResponse<ServiceDictionary>>(`${environment.healthManagerApiUrl}/dictionaries/laboratory-services`, options);
  }

  searchAchiService(searchString: string): Observable<PagedResponse<ServiceDictionary>> {
    const options = {
      params: new HttpParams().set('nameUa', searchString),
    };
    return this.http.get<PagedResponse<ServiceDictionary>>(`${environment.healthManagerApiUrl}/dictionaries/achi`, options);
  }

  getMedicationBasket(requestBody: MedicationBasketRequest): Observable<PharmacyBasketModel> {
    return this.http.post<PharmacyBasketModel>(`${environment.healthManagerApiUrl}/pharmacies`, requestBody);
  }

  createExistingGuaranteeBasket(prescriptions: Prescription[]) {
    const combinedProducts = [];
    prescriptions.forEach(prescription => {
      const medicationMetadata = prescription.metadata as PharmacologicalItemMetaData;
      const morion_items: ResponseMorionItem[] = JSON.parse(medicationMetadata.morion_items);
      const products = morion_items.map(item => {
        return {
          prescriptionId: prescription.id,
          code: item.Code.toString(),
          identifier: medicationMetadata.morion_substance || medicationMetadata.inn_ua || medicationMetadata.inn,
          substanceQuantity: item.SubsctanceQuantity,
          quantityInPackage: item.QuantityInPackage,
          form: item.FormUa,
          name: item.NameFullUa,
          requiredQuantity: +medicationMetadata.treatment_dose,
        };
      });
      combinedProducts.push(...products);
    });
    const requestBody: MedicationBasketRequest = {
      uniqueId: crypto.randomUUID(),
      products: combinedProducts,
      latitude: PHARMACIES_LATITUDE,
      longitude: PHARMACIES_LONGITUDE,
      maxDistanceInMeters: PHARMACIES_MAX_SEARCH_DISTANCE_METERS,
      requireNewFormation: true,
    };
    return requestBody;
  }

  createNewGuaranteeBasket(prescriptions: PrescriptionItem<PharmacologicalItemMetaData>[]) {
    const combinedProducts = [];
    prescriptions.forEach(prescription => {
      const medicationMetadata = prescription.metadata as PharmacologicalItemMetaData;
      const morion_items: LiloDrugSearchResponse[] = medicationMetadata.trademarks ? JSON.parse(medicationMetadata.trademarks) : [];
      const prescriptionId = crypto.randomUUID();
      const products = morion_items.map(item => {
        return {
          prescriptionId: prescriptionId,
          code: item.code,
          identifier: item.identifier,
          substanceQuantity: item.substanceQuantity,
          quantityInPackage: item.quantityInPackage,
          form: item.formUa,
          name: item.nameFullUa,
          requiredQuantity: +medicationMetadata.treatment_dose,
        };
      });
      combinedProducts.push(...products);
    });
    const requestBody: MedicationBasketRequest = {
      uniqueId: crypto.randomUUID(),
      products: combinedProducts,
      latitude: PHARMACIES_LATITUDE,
      longitude: PHARMACIES_LONGITUDE,
      maxDistanceInMeters: PHARMACIES_MAX_SEARCH_DISTANCE_METERS,
      requireNewFormation: true,
    };
    return requestBody;
  }

  createMedicationProcessBody(formValue: PrescriptionFormValue<PharmacologicalTreatmentValue>) {
    const updatedDateTime = new Date(formValue.endDate);
    updatedDateTime.setHours(23, 59, 59, 999);
    return <PrescriptionItem<PharmacologicalItemMetaData>>{
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: updatedDateTime,
      guaranteeStatus: formValue.guaranteeStatus,
      reason: formValue.reason,
      metadata: {
        single_dose: formValue.metadata.singleDose.toString(),
        form: formValue.metadata.form.toString(),
        frequency: formValue.metadata.frequency,
        treatment_dose: formValue.metadata.treatmentDose.toString(),
        daily_amount: formValue.metadata.dailyAmount.toString(),
        duration: formValue.metadata.duration.toString(),
        inn: formValue.metadata.inn,
        inn_ua: formValue.metadata.morionSubstance,
        trademarks: JSON.stringify(adaptPlainObjectToCamelCase(formValue.metadata.morionItems)),
      },
      sendGuaranteeNotification: formValue.sendGuaranteeNotification,
      franchise: formValue.franchise,
      completedInAppointment: formValue.completedInAppointment,
      completionReason: formValue.completedInAppointment ? formValue.completionReason : null,
      id: formValue.id,
      guaranteeId: formValue.guaranteeId,
      originalPrescriptionId: formValue.originalPrescriptionId,
    };
  }

  createPersonalDoctorProcessBody(formValue: PrescriptionFormValue<PersonalDoctorValue>) {
    const updatedDateTime = new Date(formValue.endDate);
    updatedDateTime.setHours(23, 59, 59, 999);
    return <PrescriptionItem<PersonalDoctorMetadata>>{
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: updatedDateTime,
      guaranteeStatus: formValue.guaranteeStatus,
      reason: formValue.reason,
      metadata: {
        service_id: formValue.metadata.serviceId,
        doctor_id: formValue.metadata.doctorId,
      },
      sendGuaranteeNotification: formValue.sendGuaranteeNotification,
      franchise: formValue.franchise,
      completedInAppointment: formValue.completedInAppointment,
      completionReason: formValue.completedInAppointment ? formValue.completionReason : null,
      id: formValue.id,
      guaranteeId: formValue.guaranteeId,
      originalPrescriptionId: formValue.originalPrescriptionId,
    };
  }

  createServiceProcessBody(formValue: PrescriptionFormValue<GenericServiceValue>) {
    const updatedDateTime = new Date(formValue.endDate);
    updatedDateTime.setHours(23, 59, 59, 999);
    return <PrescriptionItem<ServiceMetadata>>{
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: updatedDateTime,
      guaranteeStatus: formValue.guaranteeStatus,
      reason: formValue.reason,
      metadata: {
        code: formValue.metadata.code,
        quantity: formValue.metadata.quantity.toString(),
      },
      sendGuaranteeNotification: formValue.sendGuaranteeNotification,
      franchise: formValue.franchise,
      completedInAppointment: formValue.completedInAppointment,
      completionReason: formValue.completedInAppointment ? formValue.completionReason : null,
      id: formValue.id,
      guaranteeId: formValue.guaranteeId,
      originalPrescriptionId: formValue.originalPrescriptionId,
    };
  }

  getPrescriptionLimits(memberId: string, guaranteeId: string, prescriptionId: string): Observable<LimitRule[]> {
    return this.http.get<LimitRule[]>(`${environment.healthManagerApiUrl}/members/${memberId}/guarantee-applications/${guaranteeId}/prescriptions/${prescriptionId}/recalculate`);
  }

  getDobrobutDoctorSlots(prescriptionIds: string[], doctorId?: string, locationId?: string) {
    return this.http.post<DoctorWithScheduleModel[]>(`${environment.healthManagerApiUrl}/members/prescriptions/slots`, {
      doctorId,
      prescriptionIds,
      locationId,
    });
  }

  getPersonalDoctorSlots(prescriptionIds: string[], doctorId?: string, locationId?: string) {
    return this.http.post<ServiceScheduleSlotsModel[]>(`${environment.healthManagerApiUrl}/members/prescriptions/timeslots`, {
      doctorId,
      prescriptionIds,
      locationId,
    });
  }

  fetchPrescriptionFranchiseByUserId(userId: string, request: FranchiseSearchRequest): Observable<FranchiseSearchResponse> {
    return this.http.post<FranchiseSearchResponse>(`${environment.healthManagerApiUrl}/members/by-user/${userId}/franchises/search`, request);
  }

  fetchPrescriptionFranchiseByMemberId(memberId: string, request: FranchiseSearchRequest): Observable<FranchiseSearchResponse> {
    return this.http.post<FranchiseSearchResponse>(`${environment.healthManagerApiUrl}/members/${memberId}/franchises/search`, request);
  }

  POCPrescriptionsSearch(search: PrescriptionsSearch) {
    return this.http.post<PagedResponse<PrescriptionResponse>>(`${environment.healthManagerApiUrl}/prescriptions/search`, search);
  }
}
